import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken, ColumnLayout } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import {
    CustomerQuote,
    CustomerQuoteCarousel
} from "./customer-quote-carousel";
import { ConnectorLogo, LogosMarquee } from "./logos-marquee";

export type { ConnectorLogo } from "./logos-marquee";
export type { CustomerQuote } from "./customer-quote-carousel";

export interface ConnectorProps {
    title: string;
    text: string;
    linkText?: string;
    linkHref?: string;
    linkDataEventProps?: DataEventProps;
    customerQuotes: CustomerQuote[];
    mobileLogos: ConnectorLogo[];
    tabletLogos: ConnectorLogo[];
    desktopLogos: ConnectorLogo[];
}

export const Connector = ({
    title,
    text,
    linkText,
    linkHref,
    linkDataEventProps,
    customerQuotes,
    mobileLogos,
    tabletLogos,
    desktopLogos
}: ConnectorProps) => {
    return (
        <div css={containerCss}>
            <ColumnLayout layout="5-7">
                <div className="contentBlock" css={lightTextCss}>
                    <h2 css={titleCss}>{title}</h2>
                    <p css={textCss}>{text}</p>
                    {linkText && linkHref && (
                        <div css={linkCss}>
                            <Link
                                aria-label={linkText}
                                text={linkText}
                                href={linkHref}
                                type="primary"
                                bgColor="bgBlackBlue"
                                {...(linkDataEventProps && {
                                    dataEventProps: linkDataEventProps
                                })}
                            />
                        </div>
                    )}
                </div>
                <CustomerQuoteCarousel customerQuotes={customerQuotes} />
            </ColumnLayout>
            <LogosMarquee
                mobileLogos={mobileLogos}
                tabletLogos={tabletLogos}
                desktopLogos={desktopLogos}
            />
        </div>
    );
};

export default Connector;

const containerCss = css({
    paddingBlock: token("space.1000"),
    backgroundColor: wacToken("color.primary.black"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.600")
});

const lightTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, p": {
        color: wacToken("color.neutral.light0")
    },

    [media.below.sm]: {
        textAlign: "center"
    }
});

const titleCss = css({
    marginBlockStart: token("space.400"),
    marginBlockEnd: token("space.100"),

    [media.below.sm]: {
        marginBlockStart: token("space.0")
    }
});

const textCss = css({
    marginBlock: token("space.0")
});

const linkCss = css({
    marginBlockStart: token("space.300"),
    marginBlockEnd: token("space.400"),

    [media.below.sm]: {
        marginBlockEnd: token("space.300")
    }
});
